import React from "react";
import { motion } from "framer-motion";
import {
  Star,
  Crown,
  Briefcase,
  Handshake,
  Target,
  Award,
  TrendingUp,
  Package,
  FileCheck,
  Boxes,
  Truck,
  ClipboardList,
  Zap,
  HeadphonesIcon,
} from "lucide-react";
import SiteShell from "../../components/SiteShell";
import SEO from "../../components/SEO";
import { DeptHero, DeptSection, PersonCard, CardGrid } from "./_shared";

/**
 * /team/leadership — Founders & Chief Officers.
 *
 * Descriptions anchored to the Green Gold Grid mission: 720 MWh/yr BESS
 * at SIPCOT, co-owned SPV under BOOT, ≥26% community equity, ₹4-5.50/kWh
 * landed cost target, non-Chinese Indo-Pacific supply chain.
 */

const founder = {
  name: "Vishwanatha Shiva",
  role: "Founder & Chief Visionary",
  image: "/images/team/vishwanatha-shiva.jpg",
  description:
    "Threads GREEN, GOLD, and GRID into a single owned ecosystem — 720 MWh at SIPCOT under a co-owned SPV, licensed geography to geography.",
  quote:
    "The battery is the medium. The community that owns it is the message.",
};

const coFounderAndDirector = [
  {
    name: "Ganesh V",
    role: "Co-Founder & Strategic Partner",
    image: "/images/team/ganesh-v.jpg",
    description:
      "Convenes the TN government, funder consortium, and community cooperatives so the SPV can hold ≥26% resident equity at every node.",
    icon: Handshake,
  },
  {
    name: "Ragunath Padmanabhan",
    role: "Chief Revenue Officer",
    image: "/images/team/ragunath-padmanabhan.jpg",
    description:
      "Owns commercial scale — Group Captive contracts, cooperative onboarding, channel partnerships that turn ₹8-12/kWh diesel dependency into ₹4-5.50/kWh captive economics.",
    icon: TrendingUp,
  },
  {
    name: "Nagarjun.J",
    role: "Director of Operations",
    image: "/images/team/nagarjun-j.jpg",
    description:
      "Runs factory ramp and field execution — the 720 MWh/yr SIPCOT line paired with 500+ MWp of solar deployed across 31 countries.",
    icon: Target,
  },
];

const management = [
  {
    name: "Areej B",
    role: "General Manager",
    image: "/images/team/areej-b.jpg",
    description:
      "Coordinates cross-department delivery across the SPV — factory, EPC, R&D, and griddata — so every node ships on the same schedule.",
    icon: Award,
  },
  {
    name: "Dinesh C",
    role: "Finance Manager",
    image: "/images/team/dinesh-c.jpg",
    description:
      "Runs treasury and unit-economics reporting across the SPV — closes the loop between Group Captive savings and community dividend payouts.",
    icon: TrendingUp,
  },
  {
    name: "Sureshkumar.P",
    role: "Purchase, Inventory & Warehouse Coordinator",
    image: "/images/team/sureshkumar-p.jpg",
    description:
      "Supplies the 720 MWh line — cathode active material, hard-carbon precursor, cell hardware — on the non-Chinese lanes that keep the ramp on schedule.",
    icon: Package,
  },
  {
    name: "Dinesh Vijayan",
    role: "Estimation & Product Procurement",
    image: "/images/team/dinesh-vijayan.jpg",
    description:
      "Costs every 250 kWh-and-up project the SPV takes on — imported cells today, Coimbatore-made cells tomorrow — priced against the ₹4-5.50/kWh target.",
    icon: FileCheck,
  },
  {
    name: "Jayaramalingam",
    role: "Material Management",
    image: "/images/team/jayaramalingam.jpg",
    description:
      "Tracks LFP precursors, coconut-shell hard-carbon feedstock, and pack-assembly components across the factory and every active field site.",
    icon: Boxes,
  },
  {
    name: "Sudhakar",
    role: "Logistics Coordinator",
    image: "/images/team/sudhakar.jpg",
    description:
      "Moves cells, panels, and MMS steel from SIPCOT to project sites nationally — the physical spine of the horizontally-distributed footprint.",
    icon: Truck,
  },
  {
    name: "Preethy",
    role: "Administrative Assistant",
    image: "/images/team/preethy.jpg",
    description:
      "Keeps the SPV paperwork moving — statutory filings, funder tour logistics, and the weekly cadence that lets 60+ builders ship on time.",
    icon: ClipboardList,
  },
  {
    name: "Viswas K",
    role: "Sales Specialist & Technical Manager",
    description:
      "Sizes captive Solar+BESS for industrial buyers — the ₹8-12→₹4.50 case translated into a specific 250-kWh-and-up sizing on the buyer's load curve.",
    icon: Zap,
  },
  {
    name: "Radhika P",
    role: "Sales Executive",
    description:
      "Onboards RWAs, industries, and cooperatives into the co-ownership SPV structure — one signed cell reservation at a time.",
    icon: HeadphonesIcon,
  },
];

function FounderCard({ member }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className="gg-glass"
      style={{
        display: "grid",
        gridTemplateColumns: "minmax(220px, 320px) 1fr",
        gap: 28,
        padding: 28,
        borderColor: "rgba(244,169,31,0.28)",
      }}
      data-testid="founder-card"
    >
      <div>
        <img
          src={member.image}
          alt={member.name}
          loading="lazy"
          style={{
            width: "100%",
            aspectRatio: "1 / 1",
            objectFit: "cover",
            borderRadius: 18,
            border: "1px solid var(--line-strong)",
          }}
        />
        <div
          style={{
            marginTop: 14,
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "6px 12px",
            borderRadius: 999,
            background: "rgba(244,169,31,0.12)",
            border: "1px solid rgba(244,169,31,0.35)",
            color: "var(--gold-soft)",
            fontSize: 11.5,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            fontWeight: 600,
          }}
        >
          <Star size={13} strokeWidth={1.9} /> Founder
        </div>
      </div>
      <div>
        <h2
          style={{
            fontFamily: "Fraunces, Georgia, serif",
            fontSize: "clamp(24px, 3vw, 32px)",
            color: "var(--paper)",
            lineHeight: 1.15,
          }}
        >
          {member.name}
        </h2>
        <div
          style={{
            color: "var(--gold-soft)",
            fontSize: 14,
            marginTop: 6,
          }}
        >
          {member.role}
        </div>
        <p
          style={{
            color: "var(--text-dim)",
            fontSize: 15,
            lineHeight: 1.7,
            marginTop: 16,
          }}
        >
          {member.description}
        </p>
        {member.quote && (
          <p
            style={{
              color: "var(--text-cloud)",
              fontSize: 14.5,
              lineHeight: 1.6,
              marginTop: 18,
              paddingLeft: 14,
              borderLeft: "2px solid rgba(244,169,31,0.6)",
              fontStyle: "italic",
            }}
          >
            &ldquo;{member.quote}&rdquo;
          </p>
        )}
      </div>
    </motion.div>
  );
}

export default function Leadership() {
  return (
    <SiteShell>
      <SEO
        title="Leadership Team — Green Gold Energy"
        description="The founders and chief officers threading GREEN, GOLD, and GRID into a single owned energy ecosystem."
      />
      <DeptHero
        kicker="Leadership"
        title="Founders and Chief Officers"
        sub="The people threading GREEN, GOLD, and GRID into a single owned ecosystem — a 720 MWh/yr factory at SIPCOT, a co-owned SPV, and a model that licenses geography to geography."
        Icon={Crown}
      />

      <DeptSection Icon={Star} title="Our Founder">
        <FounderCard member={founder} />
      </DeptSection>

      <DeptSection Icon={Crown} title="Co-Founder & Directors">
        <CardGrid minCol={360}>
          {coFounderAndDirector.map((m) => (
            <PersonCard key={m.name} member={m} />
          ))}
        </CardGrid>
      </DeptSection>

      <DeptSection Icon={Briefcase} title="Management Team">
        <CardGrid minCol={340}>
          {management.map((m) => (
            <PersonCard key={m.name} member={m} />
          ))}
        </CardGrid>
      </DeptSection>

      <div style={{ height: 60 }} />
    </SiteShell>
  );
}
